<script setup lang="ts">
import Mousetrap, { ExtendedKeyboardEvent } from 'mousetrap';
import { onMounted, computed } from 'vue';
import { storeToRefs } from 'pinia';
import useRenditionStore from '../../stores/rendition';
import useMediaStore from '../../stores/media';
import useSettingStore from '../../stores/setting';
import useLocale from '../../composables/useLocale';
import { SKIP_BY_DEFAULT, SKIP_BY_PAGES, SKIP_BY_TIME } from '../../composables/use-navigation-controls';
import useReaderStore from '../../stores/reader';
import useNavigationTriggerStore from '../../stores/navigation-trigger';
import type { FontAwesomeIconSizes } from '../../types';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faBackward,
  faForward,
  faArrowRotateLeft,
  faArrowRotateRight,
  faArrowRotateLeft15,
  faArrowRotateRight15,
  faArrowRotateLeft30,
  faArrowRotateRight30,
  faForwardStep,
  faBackwardStep,
} from '@fortawesome/pro-solid-svg-icons';

library.add([
  faBackward,
  faForward,
  faArrowRotateLeft,
  faArrowRotateRight,
  faArrowRotateLeft15,
  faArrowRotateRight15,
  faArrowRotateLeft30,
  faArrowRotateRight30,
  faForwardStep,
  faBackwardStep,
]);

const isComingFromSlider = (e: any) =>
  e.originalTarget && e.originalTarget.classList.contains('v-slider__thumb-container');

type Emits = {
  (e: 'goToPrevious'): void;
  (e: 'goToNext'): void;
};
const emit = defineEmits<Emits>();

type Props = {
  showTooltips?: boolean;
  disabledPrevious?: boolean;
  disabledNext?: boolean;
  buttonSize?: number;
  iconSize?: FontAwesomeIconSizes;
};
withDefaults(defineProps<Props>(), {
  showTooltips: true,
  disabledPrevious: false,
  disabledNext: false,
  buttonSize: 48,
  iconSize: 'xl',
});

const { t } = useLocale();
const renditionStore = useRenditionStore();
const { isRTL } = storeToRefs(renditionStore);
const { playing, isPlaying, hasMediaAvailable } = storeToRefs(useMediaStore());
const { hasAutoPageTurn, skipBy } = storeToRefs(useSettingStore());
const { isAudioOnly } = storeToRefs(useReaderStore());
const { setNavigationTrigger } = useNavigationTriggerStore();

const previous = () => {
  if (!isPlaying.value && !hasAutoPageTurn.value && hasMediaAvailable.value) {
    playing.value = true;
  }
  setNavigationTrigger('SKIP_BUTTON_PREVIOUS');
  emit('goToPrevious');
};

const next = () => {
  if (!isPlaying.value && !hasAutoPageTurn.value && hasMediaAvailable.value) {
    playing.value = true;
  }
  setNavigationTrigger('SKIP_BUTTON_NEXT');
  emit('goToNext');
};

type SkipLabel = {
  labelKey: (dir: 'previous' | 'next') => string;
};

const skipLabelKeyMap: Record<string, SkipLabel> = {
  [SKIP_BY_PAGES]: {
    labelKey: (dir) =>
      dir === 'previous' ? 'book_player.label_skip_back_page' : 'book_player.label_skip_forward_page',
  },
  [`${SKIP_BY_TIME}_15`]: {
    labelKey: (dir) =>
      dir === 'previous' ? 'book_player.label_skip_back_15_seconds' : 'book_player.label_skip_forward_15_seconds',
  },
  [`${SKIP_BY_TIME}_30`]: {
    labelKey: (dir) =>
      dir === 'previous' ? 'book_player.label_skip_back_30_seconds' : 'book_player.label_skip_forward_30_seconds',
  },
};

const getSkipLabelKey = (direction: 'previous' | 'next'): string => {
  const headingMatch = skipBy.value.match(/^headings_(\d+)$/);
  if (headingMatch) {
    return direction === 'previous'
      ? 'book_player.label_skip_backwards_by_heading'
      : 'book_player.label_skip_forward_by_heading';
  }

  return (
    skipLabelKeyMap[skipBy.value]?.labelKey(direction) ??
    (isRTL.value
      ? direction === 'previous'
        ? 'button_web_forward'
        : 'button_web_reverse'
      : direction === 'previous'
        ? 'button_web_reverse'
        : 'button_web_forward')
  );
};

const pairIcons: Record<string, { previous: string; next: string }> = {
  [`${SKIP_BY_TIME}_15`]: {
    previous: 'fa-solid fa-arrow-rotate-left-15',
    next: 'fa-solid fa-arrow-rotate-right-15',
  },
  [`${SKIP_BY_TIME}_30`]: {
    previous: 'fa-solid fa-arrow-rotate-left-30',
    next: 'fa-solid fa-arrow-rotate-right-30',
  },
};

const getSkipIcon = (direction: 'previous' | 'next') => {
  const defaultForward = isAudioOnly.value ? 'fa-solid fa-forward-step' : 'fa-solid fa-forward';
  const defaultBackward = isAudioOnly.value ? 'fa-solid fa-backward-step' : 'fa-solid fa-backward';

  const base = isRTL.value
    ? direction === 'previous'
      ? defaultForward
      : defaultBackward
    : direction === 'previous'
      ? defaultBackward
      : defaultForward;

  let icons = { previous: base, next: base };
  if (skipBy.value !== SKIP_BY_DEFAULT) {
    icons = pairIcons[skipBy.value] ?? { previous: base, next: base };
  }

  return direction === 'previous' ? icons.previous : icons.next;
};

const getHeadingLevel = computed(() => {
  const match = skipBy.value.match(/^headings_(\d+)$/);
  return match?.[1];
});

onMounted(() => {
  // These emit the same events as the skip buttons above, but are a different way in and
  // so carry their own trigger.
  Mousetrap.bind(['alt+right', 'right'], (e: ExtendedKeyboardEvent) => {
    if (!isComingFromSlider(e)) {
      setNavigationTrigger('KEYBOARD_RIGHT');
      emit('goToNext');
    }
    return false;
  });

  Mousetrap.bind(['alt+left', 'left'], (e: ExtendedKeyboardEvent) => {
    if (!isComingFromSlider(e)) {
      setNavigationTrigger('KEYBOARD_LEFT');
      emit('goToPrevious');
    }
    return false;
  });
});
</script>

<template>
  <div class="d-flex flex-row align-center ga-2">
    <v-btn
      id="previous-btn"
      data-testid="bp-skip-previous-btn"
      :aria-label="t(getSkipLabelKey('previous'), { level: getHeadingLevel })"
      color="on-brand"
      :disabled="disabledPrevious"
      variant="text"
      :ripple="false"
      icon
      @click="previous"
      :size="buttonSize"
    >
      <FontAwesomeIcon class="mr-0" :size="iconSize" :icon="getSkipIcon('previous')" />
      <v-tooltip :disabled="!showTooltips" activator="parent" location="top">
        {{ t(getSkipLabelKey('previous'), { level: getHeadingLevel }) }}
      </v-tooltip>
    </v-btn>
    <slot></slot>
    <v-btn
      id="next-btn"
      data-testid="bp-skip-next-btn"
      :aria-label="t(getSkipLabelKey('next'), { level: getHeadingLevel })"
      color="on-brand"
      :disabled="disabledNext"
      variant="text"
      :ripple="false"
      icon
      @click="next"
      :size="buttonSize"
    >
      <FontAwesomeIcon class="ml-0" :size="iconSize" :icon="getSkipIcon('next')" />
      <v-tooltip :disabled="!showTooltips" activator="parent" location="top">
        {{ t(getSkipLabelKey('next'), { level: getHeadingLevel }) }}
      </v-tooltip>
    </v-btn>
  </div>
</template>

<style scoped lang="scss"></style>
